import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, input, ViewContainerRef } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { RestrictedElementDirective } from '@auth';
import { IncidentRuleCommon } from '@devices/models';
import { ConfirmationDialogComponent, NotificationsService } from '@ui';
import { filter, firstValueFrom } from 'rxjs';

import { IncidentRuleComponent } from '../incident-rule/incident-rule.component';
import { IncidentRuleDialogComponent } from './incident-rule-dialog';
import { IncidentRulesStore } from './incident-rules.store';
import { PopulateRuleDialogComponent } from './populate-rule-dialog/populate-rule-dialog';

@Component({
  imports: [MatCardModule, RestrictedElementDirective, MatIcon, MatSlideToggleModule, IncidentRuleComponent],
  selector: 'avs-incident-rules',
  templateUrl: './incident-rules.component.html',
})
export class IncidentRulesComponent {
  deviceId = input.required<string>();
  editable = input(true);
  protected incidentRulesStore = inject(IncidentRulesStore);
  private dialog = inject(MatDialog);
  private vcr = inject(ViewContainerRef);
  private notificationService = inject(NotificationsService);

  protected openIncidentRuleDialog(incidentRule?: IncidentRuleCommon) {
    this.dialog.open(IncidentRuleDialogComponent, {
      backdropClass: 'backdrop-modal-panel',
      data: { deviceId: this.deviceId(), incidentRule },
      viewContainerRef: this.vcr,
    });
  }

  protected deleteIncidentRule(incidentRule: IncidentRuleCommon) {
    ConfirmationDialogComponent.open(this.dialog, {
      cancelBtnText: 'Cancel',
      confirmBtnText: 'Delete',
      description: 'Are you sure you want to delete this rule? All active incidents associated with it will be resolved?',
      title: 'Delete',
    })
      .pipe(filter((result) => !!result))
      .subscribe(async () => {
        try {
          const deleted = await this.incidentRulesStore.deleteIncidentRule(incidentRule);

          if (deleted) {
            this.notificationService.showSuccessMessage(
              `Incident rule [${incidentRule.code}] was successfully deleted`,
            );
          }
        } catch (e) {
          this.notificationService.showErrorMessage((e as HttpErrorResponse).message);
        }
      });
  }

  protected async toggleIncidentRule({ enabled, revert, rule }: { enabled: boolean; revert: () => void; rule: IncidentRuleCommon }) {
    if (!enabled) {
      const confirmed = await firstValueFrom(
        ConfirmationDialogComponent.open(this.dialog, {
          cancelBtnText: 'Cancel',
          confirmBtnText: 'Deactivate',
          description: 'Are you sure you want to deactivate this rule? All active incidents associated with it will be resolved.',
          title: 'Deactivate',
        }),
      );

      if (!confirmed) {
        revert();

        return;
      }
    }

    try {
      const updatedWorkflow = await this.incidentRulesStore.updateIncidentRule(rule.id, { enabled });

      if (updatedWorkflow) {
        this.notificationService.showSuccessMessage(
          `Incident rule [${rule.code}] was successfully updated`,
        );
      }
    } catch (e) {
      this.notificationService.showErrorMessage((e as HttpErrorResponse).message);
    }
  }

  protected shareIncidentRule(incidentRule: IncidentRuleCommon) {
    this.dialog.open(PopulateRuleDialogComponent, {
      backdropClass: 'backdrop-modal-panel',
      data: {
        deviceId: this.deviceId(),
        incidentRule,
      },
      viewContainerRef: this.vcr,
    });
  }
}
